/**
 * The diagnostics check functions (web-client.md §8). Each is a small, pure/async function over
 * injected inputs so it is unit-testable against fakes (a passing WS handshake, a failing API, a
 * skewed clock, a corrupt store → the right pass/warn/fail). No DOM, no direct network — the app
 * layer supplies the probes. Nothing here reads a token.
 */
import type { VersionManifest } from '../rest/versionClient.ts';
import type { AuthSnapshot, Capabilities, CheckResult, StorageProbe } from './types.ts';

/** Latency above this is a warn (still reachable, but slow). */
export const LATENCY_WARN_MS = 750;
/** Absolute clock skew above this is a warn; above {@link CLOCK_FAIL_MS} a fail. */
export const CLOCK_WARN_MS = 2_000;
export const CLOCK_FAIL_MS = 60_000;

function ok(id: string, label: string, detail: string): CheckResult {
  return { id, label, status: 'pass', detail };
}

/** version + protocol check against the fetched manifest (protocol.md §11). */
export function versionCheck(input: {
  clientVersion: string;
  clientProtocol: number;
  manifest: VersionManifest;
  clientType?: string;
}): CheckResult {
  const { clientVersion, clientProtocol, manifest } = input;
  const info = manifest.clients[input.clientType ?? 'web'];
  const detail =
    `client ${clientVersion} (proto ${clientProtocol}) · ` +
    `server proto ${manifest.protocolVersion} (min ${manifest.minProtocolVersion})` +
    (info ? ` · latest ${info.latestVersion}, min ${info.minSupportedVersion}` : '');
  if (clientProtocol < manifest.minProtocolVersion) {
    return {
      id: 'version',
      label: 'Version & protocol',
      status: 'fail',
      detail: `protocol too old — ${detail}`,
    };
  }
  return ok('version', 'Version & protocol', detail);
}

/** API reachability + latency. `probe` resolves the round-trip ms or rejects when unreachable. */
export async function apiCheck(probe: () => Promise<number>): Promise<CheckResult> {
  const label = 'API reachability';
  try {
    const latencyMs = await probe();
    const detail = `reachable · ${Math.round(latencyMs)} ms`;
    if (latencyMs > LATENCY_WARN_MS) {
      return { id: 'api', label, status: 'warn', detail: `slow — ${detail}` };
    }
    return ok('api', label, detail);
  } catch (error) {
    return { id: 'api', label, status: 'fail', detail: `unreachable — ${errText(error)}` };
  }
}

/** WS handshake round-trip: `probe` opens, sends sys.hello, measures ms to sys.welcome, closes. */
export async function wsHandshakeCheck(probe: () => Promise<number>): Promise<CheckResult> {
  const label = 'WS handshake';
  try {
    const rttMs = await probe();
    const detail = `sys.welcome in ${Math.round(rttMs)} ms`;
    if (rttMs > LATENCY_WARN_MS) {
      return { id: 'ws', label, status: 'warn', detail: `slow — ${detail}` };
    }
    return ok('ws', label, detail);
  } catch (error) {
    return { id: 'ws', label, status: 'fail', detail: `handshake failed — ${errText(error)}` };
  }
}

/** auth/token presence + refresh timing (never reads the token itself). */
export function authCheck(snapshot: AuthSnapshot): CheckResult {
  const label = 'Auth & token';
  if (!snapshot.authenticated) {
    return {
      id: 'auth',
      label,
      status: 'skip',
      detail: 'not signed in (connectivity checks only)',
    };
  }
  const expiresIn = snapshot.expiresInMs ?? 0;
  const secs = Math.round(expiresIn / 1000);
  if (expiresIn <= 0) {
    return { id: 'auth', label, status: 'warn', detail: 'access token expired — refresh pending' };
  }
  const idPart = snapshot.playerIdPresent ? '' : ' · no playerId';
  return ok('auth', label, `token valid · expires in ${secs}s${idPart}`);
}

/** local-storage / IndexedDB integrity. `probe` round-trips a value through each store. */
export async function storageCheck(probe: () => Promise<StorageProbe>): Promise<CheckResult> {
  const label = 'Storage integrity';
  try {
    const result = await probe();
    if (result.localStore && result.indexedDb) {
      return ok('storage', label, 'local store + IndexedDB OK');
    }
    const broken = [
      !result.localStore ? 'local store' : null,
      !result.indexedDb ? 'IndexedDB' : null,
    ]
      .filter(Boolean)
      .join(', ');
    return {
      id: 'storage',
      label,
      status: 'warn',
      detail: `unavailable: ${broken} (offer repair)`,
    };
  } catch (error) {
    return { id: 'storage', label, status: 'fail', detail: `probe threw — ${errText(error)}` };
  }
}

/** clock skew: client vs server /time. `skewMs` = clientTime − serverTime (signed). */
export function clockSkewCheck(input: { skewMs: number }): CheckResult {
  const label = 'Clock skew';
  const abs = Math.abs(input.skewMs);
  const sign = input.skewMs >= 0 ? 'ahead' : 'behind';
  const detail = `${Math.round(abs)} ms ${sign} of server`;
  if (abs > CLOCK_FAIL_MS)
    return { id: 'clock', label, status: 'fail', detail: `large skew — ${detail}` };
  if (abs > CLOCK_WARN_MS) return { id: 'clock', label, status: 'warn', detail };
  return ok('clock', label, detail);
}

/** effective feature flags (from sys.welcome, held in the store). */
export function flagsCheck(flags: Record<string, boolean>): CheckResult {
  const label = 'Effective flags';
  const on = Object.entries(flags)
    .filter(([, v]) => v)
    .map(([k]) => k);
  const count = Object.keys(flags).length;
  if (count === 0) {
    return { id: 'flags', label, status: 'skip', detail: 'no flags yet (connect to load)' };
  }
  return ok('flags', label, `${on.length}/${count} on: ${on.join(', ') || '(none)'}`);
}

/** capability report: SW support, WebSocket, storage quota, reduced-motion (web-client.md §8). */
export function capabilityCheck(caps: Capabilities): CheckResult {
  const label = 'Capabilities';
  const missing: string[] = [];
  if (!caps.serviceWorker) missing.push('service worker');
  if (!caps.webSocket) missing.push('WebSocket');
  const quota =
    caps.storageQuotaBytes != null
      ? `${Math.round(caps.storageQuotaBytes / 1_048_576)} MB quota`
      : 'quota n/a';
  const motion = caps.prefersReducedMotion ? 'reduced-motion' : 'full-motion';
  const detail = `SW ${yn(caps.serviceWorker)} · WS ${yn(caps.webSocket)} · IDB ${yn(
    caps.indexedDb,
  )} · LS ${yn(caps.localStore)} · ${quota} · ${motion}`;
  // WebSocket is load-bearing; its absence is a hard fail. A missing SW only degrades offline/update.
  if (!caps.webSocket) return { id: 'caps', label, status: 'fail', detail };
  if (missing.length) return { id: 'caps', label, status: 'warn', detail };
  return ok('caps', label, detail);
}

function yn(v: boolean): string {
  return v ? '✓' : '✗';
}

function errText(error: unknown): string {
  if (error instanceof Error) return error.message;
  return String(error);
}
